import { useState, useCallback } from "react";
import { useAuth } from "./useAuth";
import { MealPlanPreferences, MealPlan, MealType, CustomMeal, PenciledMealMemory } from "../types/mealPlan";
import { handleUsageLimitError } from "../utils/usageLimitNotifications";

export const useMealPlanning = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMealPlan = useCallback(
    async (
      preferences: MealPlanPreferences,
      startDate: string,
      endDate: string
    ): Promise<MealPlan | null> => {
      if (!user) {
        setError("User must be authenticated");
        return null;
      }

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ preferences, startDate, endDate }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();

          // Handle 429 usage limit errors with user-friendly notifications
          if (handleUsageLimitError(response, errorData)) {
            return null;
          }

          throw new Error(errorData.error || "Failed to create meal plan");
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error creating meal plan:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const getMealPlan = useCallback(
    async (
      userId: string,
      planId: string,
      skipLoadingState = false
    ): Promise<{
      mealPlan: MealPlan;
      owner?: { uid: string; displayName?: string; photoURL?: string };
      isOwner?: boolean;
    } | null> => {
      if (!skipLoadingState) {
        setLoading(true);
        setError(null);
      }

      try {
        const headers: HeadersInit = {
          "Content-Type": "application/json",
        };

        // Include auth token if user is logged in
        if (user) {
          const token = await user.getIdToken();
          headers.Authorization = `Bearer ${token}`;
        }

        // Use friendly URL endpoint when userId is not available (slug route)
        const apiUrl = userId
          ? `${process.env.REACT_APP_API_URL}/api/u/${userId}/plan/${planId}`
          : `${process.env.REACT_APP_API_URL}/api/plan/${planId}`;

        const response = await fetch(apiUrl, { headers });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to fetch meal plan");
        }

        const data = await response.json();
        return data;
      } catch (error: any) {
        console.error("Error fetching meal plan:", error);
        if (!skipLoadingState) {
          setError(error.message);
        }
        return null;
      } finally {
        if (!skipLoadingState) {
          setLoading(false);
        }
      }
    },
    [user]
  );

  const getUserMealPlans = useCallback(async (): Promise<MealPlan[]> => {
    if (!user) return [];

    setLoading(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meals/plans`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch meal plans");
      }

      const data = await response.json();
      return data.mealPlans;
    } catch (error: any) {
      console.error("Error fetching meal plans:", error);
      setError(error.message);
      return [];
    } finally {
      setLoading(false);
    }
  }, [user]);

  const activateMealPlan = useCallback(
    async (planId: string): Promise<boolean> => {
      if (!user) return false;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/activate`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to activate meal plan");
        }

        return true;
      } catch (error: any) {
        console.error("Error activating meal plan:", error);
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const regenerateGroceryList = useCallback(
    async (planId: string): Promise<boolean> => {
      if (!user) return false;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/regenerate-grocery-list`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to regenerate grocery list");
        }

        return true;
      } catch (error: any) {
        console.error("Error regenerating grocery list:", error);
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const substituteRecipe = useCallback(
    async (
      planId: string,
      dayToReplace: string,
      newRecipeId?: string,
      instructions?: string,
      modifyExisting?: boolean,
      reasons?: string[],
      mealType?: MealType
    ): Promise<MealPlan | null> => {
      if (!user) return null;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/substitute`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              dayToReplace,
              // Omitted = dinner on the backend; only sent for non-dinner
              // slots so old backend deploys keep working
              mealType:
                mealType && mealType !== "dinner" ? mealType : undefined,
              newRecipeId,
              instructions,
              modifyExisting,
              reasons: reasons && reasons.length > 0 ? reasons : undefined,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();

          // Handle 429 usage limit errors with user-friendly notifications
          if (handleUsageLimitError(response, errorData)) {
            return null;
          }

          throw new Error(errorData.error || "Failed to substitute recipe");
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error substituting recipe:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const saveRecipeToUserCookbook = useCallback(
    async (recipeId: string): Promise<boolean> => {
      if (!user) return false;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipeId}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to save recipe to cookbook"
          );
        }

        return true;
      } catch (error: any) {
        console.error("Error saving recipe to cookbook:", error);
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const removeRecipeFromUserCookbook = useCallback(
    async (recipeId: string): Promise<boolean> => {
      if (!user) return false;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/recipes/cookbook/${recipeId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to remove recipe from cookbook"
          );
        }

        return true;
      } catch (error: any) {
        console.error("Error removing recipe from cookbook:", error);
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  // Fill an empty breakfast/lunch slot: with a cookbook recipe (newRecipeId)
  // or a generated pick when omitted
  const addMealPlanAssignment = useCallback(
    async (
      planId: string,
      plannedDate: string,
      mealType: MealType,
      newRecipeId?: string
    ): Promise<MealPlan | null> => {
      if (!user) return null;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/assignments`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ plannedDate, mealType, newRecipeId }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to add meal to plan");
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error adding meal plan assignment:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const deleteMealPlanAssignment = useCallback(
    async (
      planId: string,
      plannedDate: string,
      mealType?: MealType
    ): Promise<MealPlan | null> => {
      if (!user) return null;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/assignments`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              plannedDate,
              mealType:
                mealType && mealType !== "dinner" ? mealType : undefined,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to delete meal plan assignment"
          );
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error deleting meal plan assignment:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const undoDeleteMealPlanAssignment = useCallback(
    async (
      planId: string,
      plannedDate: string,
      mealType?: MealType
    ): Promise<MealPlan | null> => {
      if (!user) return null;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/assignments/undo`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              plannedDate,
              mealType:
                mealType && mealType !== "dinner" ? mealType : undefined,
            }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.error || "Failed to restore meal plan assignment"
          );
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error restoring meal plan assignment:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const addCustomMealAssignment = useCallback(
    async (
      planId: string,
      plannedDate: string,
      customMeal: CustomMeal,
      replaceExisting: boolean = false,
      mealType: 'breakfast' | 'lunch' | 'dinner' = 'dinner'
    ): Promise<MealPlan | null> => {
      if (!user) return null;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/assignments`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ plannedDate, customMeal, replaceExisting, mealType }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to add meal to plan");
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error adding custom meal:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  const updateCustomMealAssignment = useCallback(
    async (
      planId: string,
      assignmentId: string,
      customMeal: CustomMeal
    ): Promise<MealPlan | null> => {
      if (!user) return null;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}/assignments/${assignmentId}/custom-meal`,
          {
            method: "PUT",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ customMeal }),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update meal");
        }

        const data = await response.json();
        return data.mealPlan;
      } catch (error: any) {
        console.error("Error updating custom meal:", error);
        setError(error.message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  // Remembered penciled-in meals for the pencil-in sheet. Quiet fetch: no
  // loading/error state churn, since the sheet renders fine without it.
  /**
   * The most recent request this household wrote, for the wizard's "use last
   * week's request" chip. Null when they have never written one.
   *
   * Its own endpoint rather than a read off getUserMealPlans: that response
   * carries every assignment and scaled recipe, and the wizard already pays
   * for it once in the week-shape step.
   */
  const getLastCustomRequest = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meals/last-custom-request`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) return null;
      const data = await response.json();
      return data.customRequest ?? null;
    } catch (error) {
      console.error("Error fetching last custom request:", error);
      return null;
    }
  }, [user]);

  const getPenciledMeals = useCallback(async (): Promise<PenciledMealMemory[]> => {
    if (!user) return [];
    try {
      const token = await user.getIdToken();
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/meals/penciled-meals`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      if (!response.ok) return [];
      const data = await response.json();
      return data.penciledMeals ?? [];
    } catch (error) {
      console.error("Error fetching penciled meals:", error);
      return [];
    }
  }, [user]);

  const deleteMealPlan = useCallback(
    async (planId: string): Promise<boolean> => {
      if (!user) return false;

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to delete meal plan");
        }

        console.log(
          "About to return true from deleteMealPlan - API call succeeded"
        );
        return true;
      } catch (error: any) {
        console.error(
          "Error deleting meal plan - entering catch block:",
          error
        );
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  // Resolves to whether the update landed (the endpoint's body is just an
  // ack): callers apply changes optimistically and roll back on false.
  const updateMealPlan = useCallback(
    async (planId: string, updates: Partial<MealPlan>): Promise<boolean> => {
      if (!user) {
        setError("User must be authenticated");
        return false;
      }

      setLoading(true);
      setError(null);

      try {
        const token = await user.getIdToken();
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/meals/plans/${planId}`,
          {
            method: "PATCH",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(updates),
          }
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || "Failed to update meal plan");
        }

        return true;
      } catch (error: any) {
        console.error("Error updating meal plan:", error);
        setError(error.message);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [user]
  );

  return {
    createMealPlan,
    getMealPlan,
    getUserMealPlans,
    activateMealPlan,
    regenerateGroceryList,
    substituteRecipe,
    saveRecipeToUserCookbook,
    removeRecipeFromUserCookbook,
    addMealPlanAssignment,
    deleteMealPlanAssignment,
    undoDeleteMealPlanAssignment,
    addCustomMealAssignment,
    updateCustomMealAssignment,
    getLastCustomRequest,
    getPenciledMeals,
    deleteMealPlan,
    updateMealPlan,
    loading,
    error,
  };
};
